import { Field, FieldProps, FormikHelpers, FormikProps } from "formik";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Skeleton,
  Spacer,
} from "@chakra-ui/react";
import { toTitle } from "@/utils/case";
import AppSeasonSelect from "@/features/line-sheet-sets/app-season-select";
import React, { useCallback, useMemo } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import {
  AbstractInvoice,
  PaymentMethod,
} from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice.type";
import AppPaymentMethodSelect from "@/features/ui/app-payment-method-select";
import AppPaidByAgencySelect from "@/features/ui/app-paid-by-agency-select";
import AppTextArea from "@/features/ui/app-text-area";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import AppInput from "@/features/ui/app-input";
import AppRemittanceStatusLabel from "@/features/ui/app-remittance-status-label";
import AppInvoiceStatusSelect from "@/features/invoices/order-confirmation-invoices/app-invoice-status-select";
import { OrderConfirmationInvoiceFormFields } from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-detail-page";
import AppButton from "@/features/line-sheet-sets/app-button";
import useDisclosureWithData from "@/features/order-sheet-sets/hooks/use-disclosure-with-data";
import CreateCompanyModal, {
  CreateCompanyModalProps,
} from "@/features/ui/create-company-modal";
import useIdentity from "@/features/ui/hooks/use-identity";
import AppInvoiceStatusLabel from "@/features/ui/app-invoice-status-label";

interface OrderConfirmationInvoiceInformationFormControlProps<T> {
  formik: FormikProps<T>;
  isEditing?: boolean;
  isDisabled?: boolean;
}

interface FormFields extends OrderConfirmationInvoiceFormFields {}

export default function OrderConfirmationInvoiceInformationFormControl<
  T extends FormFields
>({
  formik,
  isEditing,
  isDisabled,
}: OrderConfirmationInvoiceInformationFormControlProps<T>) {
  const identity = useIdentity();
  const { t, tTitle } = useI18nHelper();

  type LINK_AFFECTING_KEYS =
    | "season"
    | "issuedBy"
    | "orderedBy"
    | "issuedThrough";

  const handleLinkAffectingFieldChange = useCallback(
    <T extends LINK_AFFECTING_KEYS>(
      key: LINK_AFFECTING_KEYS,
      value: FormFields[T],
      setValues: FormikHelpers<FormFields>["setValues"]
    ) => {
      return setValues((prev) => {
        let next = {
          ...prev,
          [key]: value,
          orderSheetSets: [],
        };

        next.orderConfirmationInvoiceDetailList =
          next.orderConfirmationInvoiceDetailList.map((ocDetail) => {
            return {
              ...ocDetail,
              brand: ocDetail.brand,
              orderSheetSet: undefined,
            };
          });

        return next;
      });
    },
    []
  );

  const {
    isOpen: isCreateCompanyModelOpen,
    onClose: onCreateCompanyModalClose,
    data: createCompanyModalData,
    openWithData: openCreateCompanyModal,
  } = useDisclosureWithData<CreateCompanyModalProps>();

  if (!formik.values.id) {
    return (
      <AppPanel title={<Skeleton height={"32px"} />}>
        <Grid
          rowGap={"8px"}
          gridTemplateRows={"repeat(7, 16px 24px) repeat(2, 16px 60px)"}
        >
          {(Array(18).fill(0) as number[]).map((_, index) => {
            return <Skeleton key={index} />;
          })}
        </Grid>
      </AppPanel>
    );
  }

  return (
    <AppPanel title={tTitle("common:invoice_information.abbr")}>
      <Grid rowGap={"8px"}>
        {!isEditing && (
          <>
            <Field name={"status"}>
              {(props: FieldProps<FormFields["status"], FormFields>) => {
                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"row"}
                    gap={"4px"}
                    isRequired={isEditing}
                    fontSize={"12px"}
                  >
                    <FormLabel
                      fontSize={"12px"}
                      color={"#6F6F6C"}
                      padding={"0"}
                      margin={"0"}
                    >
                      {toTitle(t("status"))}
                    </FormLabel>
                    <Spacer />
                    <AppInvoiceStatusLabel value={props.field.value} />
                  </FormControl>
                );
              }}
            </Field>

            <Field name={"isRemittedToBoutique"}>
              {(
                props: FieldProps<
                  FormFields["isRemittedToBoutique"],
                  FormFields
                >
              ) => {
                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"row"}
                    gap={"4px"}
                    isRequired={isEditing}
                    fontSize={"12px"}
                  >
                    <FormLabel
                      fontSize={"12px"}
                      color={"#6F6F6C"}
                      padding={"0"}
                      margin={"0"}
                    >
                      {toTitle(t("remittance_status_to_boutique"))}
                    </FormLabel>
                    <Spacer />
                    <AppRemittanceStatusLabel
                      value={
                        props.field.value !== undefined
                          ? props.field.value
                            ? "REMITTED"
                            : "UNREMITTED"
                          : undefined
                      }
                    />
                  </FormControl>
                );
              }}
            </Field>

            {formik.values.paidByAgency && (
              <Field name={"isRemittedToAgency"}>
                {(
                  props: FieldProps<
                    FormFields["isRemittedToAgency"],
                    FormFields
                  >
                ) => {
                  return (
                    <FormControl
                      display={"flex"}
                      flexDirection={"row"}
                      gap={"4px"}
                      isRequired={isEditing}
                      fontSize={"12px"}
                    >
                      <FormLabel
                        fontSize={"12px"}
                        color={"#6F6F6C"}
                        padding={"0"}
                        margin={"0"}
                      >
                        {toTitle(t("common:remittance_status_to_agency"))}
                      </FormLabel>
                      <Spacer />
                      <AppRemittanceStatusLabel
                        value={
                          props.field.value !== undefined
                            ? props.field.value
                              ? "REMITTED"
                              : "UNREMITTED"
                            : undefined
                        }
                      />
                    </FormControl>
                  );
                }}
              </Field>
            )}

            <Box
              height={"8px"}
              borderBottom={"0.5px solid var(--Pathrade-Gray-Groomy, #8F8F8C)"}
            ></Box>
          </>
        )}

        <Field name={"name"}>
          {(props: FieldProps<FormFields["name"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                isRequired={isEditing}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("title"))}
                </FormLabel>
                <AppInput
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        <Field name={"number"}>
          {(props: FieldProps<FormFields["number"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                isRequired={isEditing}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("invoice_number"))}
                </FormLabel>
                <AppInput
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        {identity?.company?.type !== "BOUTIQUE" && isEditing && (
          <Field name={"status"}>
            {(props: FieldProps<FormFields["status"], FormFields>) => {
              const paymentMethod = props.form.values.paymentMethod;
              //입금 && 예상잔여디파짓이 0이면 => 변경 불가능 (서버는 completed로 주었어야함)
              //SKIP이면 항상 수정가능

              let excludeStatuses: AbstractInvoice["status"][] = ["REPLACED"];

              if (props.form.values.id === -1) {
                excludeStatuses = [...excludeStatuses, "CANCELED", "COMPLETED"];
              }

              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"4px"}
                  isRequired={isEditing}
                  fontSize={"12px"}
                >
                  <FormLabel
                    fontSize={"12px"}
                    color={"#6F6F6C"}
                    padding={"0"}
                    margin={"0"}
                  >
                    {toTitle(t("status"))}
                  </FormLabel>
                  <AppInvoiceStatusSelect
                    value={props.field.value}
                    isReadOnly={!isEditing}
                    excludeStatuses={excludeStatuses}
                    onSelect={(value) => {
                      props.form.setValues((prev) => {
                        return {
                          ...prev,
                          status: value,
                        };
                      });
                    }}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}

        <Field name={"season"}>
          {(props: FieldProps<string, FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
                isRequired={isEditing}
                fontSize={"12px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("season"))}
                </FormLabel>
                <AppSeasonSelect
                  width={"100%"}
                  value={props.field.value}
                  isDisabled={isDisabled}
                  readonly={!isEditing}
                  onSelect={(value) => {
                    handleLinkAffectingFieldChange(
                      "season",
                      value,
                      props.form.setValues
                    );
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        {identity?.company?.type !== "BUYER" && (
          <Field name={"orderedBy"}>
            {(props: FieldProps<FormFields["orderedBy"], FormFields>) => {
              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"4px"}
                  isRequired={isEditing}
                >
                  <FormLabel
                    fontSize={"12px"}
                    color={"#6F6F6C"}
                    padding={"0"}
                    margin={"0"}
                  >
                    {toTitle(t("ordered_by"))}
                  </FormLabel>
                  <AppCompanySelect
                    name={tTitle("ordered_by")}
                    types={["BUYER"]}
                    width={"100%"}
                    id={props.field.value?.id}
                    isReadOnly={!isEditing}
                    isDisabled={isDisabled}
                    onSelect={(value) => {
                      handleLinkAffectingFieldChange(
                        "orderedBy",
                        value,
                        props.form.setValues
                      );
                    }}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}

        {identity?.company?.type !== "AGENCY" &&
          identity?.company?.type !== "BOUTIQUE" && (
            <Field name={"issuedThrough"}>
              {(props: FieldProps<FormFields["issuedThrough"], FormFields>) => {
                return (
                  <Flex flexDirection={"row"} alignItems={"end"} gap={"8px"}>
                    <FormControl
                      display={"flex"}
                      flexDirection={"column"}
                      gap={"4px"}
                      isRequired={isEditing}
                    >
                      <FormLabel
                        fontSize={"12px"}
                        color={"#6F6F6C"}
                        padding={"0"}
                        margin={"0"}
                      >
                        {toTitle(t("issued_through"))}
                      </FormLabel>

                      <AppCompanySelect
                        name={tTitle("issued_through")}
                        types={["AGENCY"]}
                        width={"100%"}
                        id={props.field.value?.id}
                        isReadOnly={!isEditing}
                        isDisabled={isDisabled}
                        onSelect={(value) => {
                          handleLinkAffectingFieldChange(
                            "issuedThrough",
                            value,
                            props.form.setValues
                          );
                        }}
                      />
                    </FormControl>
                    {isEditing && (
                      <AppButton
                        onClick={() => {
                          openCreateCompanyModal(() => {
                            return {
                              targetBy: "AGENCY",
                              onCreate: (value) => {
                                if (!isDisabled) {
                                  handleLinkAffectingFieldChange(
                                    "issuedThrough",
                                    value,
                                    props.form.setValues
                                  );
                                }
                              },
                            };
                          });
                        }}
                      >
                        {tTitle("add")}
                      </AppButton>
                    )}
                  </Flex>
                );
              }}
            </Field>
          )}

        {identity?.company?.type !== "BOUTIQUE" && (
          <Field name={"issuedBy"}>
            {(props: FieldProps<FormFields["issuedBy"], FormFields>) => {
              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"4px"}
                  isRequired={isEditing}
                >
                  <FormLabel
                    fontSize={"12px"}
                    color={"#6F6F6C"}
                    padding={"0"}
                    margin={"0"}
                  >
                    {toTitle(t("issued_by"))}
                  </FormLabel>
                  <AppCompanySelect
                    name={toTitle(t("issued_by"))}
                    types={["BOUTIQUE"]}
                    width={"100%"}
                    id={props.field.value?.id}
                    isDisabled={isDisabled}
                    isReadOnly={!isEditing}
                    onSelect={(value) => {
                      handleLinkAffectingFieldChange(
                        "issuedBy",
                        value,
                        props.form.setValues
                      );
                    }}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}

        <Field name={"issuedOn"}>
          {(props: FieldProps<Date, FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                isRequired={isEditing}
                gap={"4px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {toTitle(t("issued_on"))}
                </FormLabel>
                <AppDatePicker
                  width={"100%"}
                  showTimeInput={false}
                  name={tTitle("issued_on")}
                  value={props.field.value}
                  isReadOnly={!isEditing}
                  isDisabled={isDisabled}
                  onChange={(value) => {
                    props.form.setFieldValue("issuedOn", value);
                  }}
                />
              </FormControl>
            );
          }}
        </Field>

        {identity?.company?.type !== "BOUTIQUE" && (
          <Field name={"paymentMethod"}>
            {(props: FieldProps<PaymentMethod | undefined, FormFields>) => {
              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  isRequired={isEditing}
                  gap={"4px"}
                >
                  <FormLabel
                    fontSize={"12px"}
                    color={"#6F6F6C"}
                    padding={"0"}
                    margin={"0"}
                  >
                    {toTitle(t("payment_method"))}
                  </FormLabel>
                  <AppPaymentMethodSelect
                    value={props.field.value}
                    isDisabled={isDisabled}
                    onSelect={(value) => {
                      props.form.setValues((prev) => {
                        return {
                          ...prev,
                          paymentMethod: value,
                          paidByAgency:
                            value === "LOAN" ? true : prev.paidByAgency,
                        };
                      });
                    }}
                    isReadOnly={!isEditing}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}

        {identity?.company?.type === "AGENCY" && (
          <Field name={"exposedToBuyer"}>
            {(props: FieldProps<FormFields["exposedToBuyer"], FormFields>) => {
              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"4px"}
                >
                  <AppCheckBox
                    isChecked={props.field.value}
                    isDisabled={!isEditing}
                    name={toTitle(t("exposed_to_buyer"))}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}

        {identity?.company?.type !== "BOUTIQUE" && (
          <Field name={"paidByAgency"}>
            {(props: FieldProps<FormFields["paidByAgency"], FormFields>) => {
              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"4px"}
                  isRequired={isEditing}
                >
                  <FormLabel
                    fontSize={"12px"}
                    color={"#6F6F6C"}
                    padding={"0"}
                    margin={"0"}
                  >
                    {toTitle(t("paid_by"))}
                  </FormLabel>
                  <AppPaidByAgencySelect
                    value={props.field.value}
                    isReadOnly={!isEditing}
                    isDisabled={
                      isEditing &&
                      (isDisabled || props.form.values.paymentMethod === "LOAN")
                    }
                    onSelect={(value) => {
                      props.form.setFieldValue("paidByAgency", value);
                    }}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}

        <Field name={"publicComment"}>
          {(props: FieldProps<FormFields["publicComment"], FormFields>) => {
            return (
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                gap={"4px"}
              >
                <FormLabel
                  fontSize={"12px"}
                  color={"#6F6F6C"}
                  padding={"0"}
                  margin={"0"}
                >
                  {identity?.company?.type === "AGENCY"
                    ? `${toTitle(t("public_comment"))}(${t(
                        "public_comment.note"
                      )})`
                    : `${toTitle(t("comment"))}`}
                </FormLabel>
                <AppTextArea
                  flexGrow={"1"}
                  fontSize={"12px"}
                  rows={4}
                  placeholder={isEditing ? t("comment_placeholder") : undefined}
                  value={props.field.value || ""}
                  onChange={(e) => {
                    props.form.setFieldValue("publicComment", e.target.value);
                  }}
                  isReadOnly={!isEditing}
                />
              </FormControl>
            );
          }}
        </Field>

        {identity?.company?.type === "AGENCY" && (
          <Field name={"privateComment"}>
            {(props: FieldProps<FormFields["privateComment"], FormFields>) => {
              return (
                <FormControl
                  display={"flex"}
                  flexDirection={"column"}
                  gap={"4px"}
                >
                  <FormLabel
                    fontSize={"12px"}
                    color={"#6F6F6C"}
                    marginBottom={"4px"}
                  >
                    {toTitle(t("private_comment"))}
                  </FormLabel>
                  <AppTextArea
                    flexGrow={"1"}
                    minHeight={"0"}
                    fontSize={"12px"}
                    rows={4}
                    placeholder={
                      isEditing ? t("comment_placeholder") : undefined
                    }
                    value={props.field.value || ""}
                    onChange={(e) => {
                      props.form.setFieldValue(
                        "privateComment",
                        e.target.value
                      );
                    }}
                    isReadOnly={!isEditing}
                  />
                </FormControl>
              );
            }}
          </Field>
        )}
      </Grid>
      {isCreateCompanyModelOpen && createCompanyModalData && (
        <CreateCompanyModal
          isOpen={isCreateCompanyModelOpen}
          onClose={onCreateCompanyModalClose}
          {...createCompanyModalData}
        />
      )}
    </AppPanel>
  );
}
